import {
  CustomInput,
  CustomSelect,
  CustomSlider,
  CustomTextarea,
  CustomToggle,
} from '../Components/CustomFormElements';
import VoiceSamplePlayer from '../Components/VoiceSamplePlayer';
import { VoiceProfileForm as FormState, VoiceProfileFormProps } from './VoiceProfileHelpers';

function VoiceProfileForm({
  form,
  agents,
  voices,
  languages,
  isDisabled = false,
  voicesLoading = false,
  onRefreshVoices,
  onChange,
}: VoiceProfileFormProps) {
  const set = (partial: Partial<FormState>) => onChange({ ...form, ...partial });

  // "Auto detect" carries an empty value and the primary language is already
  // chosen, so neither belongs in the secondary list.
  const selectedVoice = voices.find((voice) => voice.value === form.voice);

  const secondaryLanguageOptions = languages.filter(
    (language) => language.value && language.value !== form.languageCode,
  );

  return (
    <>
      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Name</div>
          <div className="small-font">Enter voice profile name</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomInput
            id="voice-profile-name"
            label="Voice Profile Name"
            placeholder="Enter voice profile name"
            icon={<i className="fa-solid fa-comments"></i>}
            value={form.name}
            onChange={(_, value) => set({ name: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Agent</div>
          <div className="small-font">Select an agent</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="agent-select"
            label="Agent"
            placeholder="Select an agent"
            options={agents.map((a) => ({ value: a.key, label: a.name }))}
            value={form.assistantKey}
            multiSelect={false}
            onChange={(value) => set({ assistantKey: value as string })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Voice Type</div>
          <div className="small-font">Select a voice and play its sample (optional)</div>
        </div>
        <div className="align-wrapper d-flex align-center" style={{ gap: 8 }}>
          <CustomSelect
            id="voice-select"
            label="Voice"
            placeholder="Select a voice"
            options={voices}
            value={form.voice}
            multiSelect={false}
            onChange={(value) => set({ voice: value as string })}
          />
          <VoiceSamplePlayer url={selectedVoice?.previewUrl} />
          {onRefreshVoices && (
            <button
              type="button"
              className="btn btn-secondary btn-small"
              onClick={onRefreshVoices}
              disabled={isDisabled || voicesLoading}
              title="Reload voices from ElevenLabs"
              aria-label="Reload voices"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden="true"
              >
                <path d="M17.65 6.35A7.96 7.96 0 0 0 12 4a8 8 0 1 0 7.73 10h-2.08A6 6 0 1 1 12 6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" />
              </svg>
            </button>
          )}
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Input Language</div>
          <div className="small-font">Language of the caller&apos;s speech</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="language-select"
            label="Input Language"
            placeholder="Select an input language"
            options={languages}
            value={form.languageCode}
            multiSelect={false}
            onChange={(value) => {
              const languageCode = (value as string) ?? '';
              set({
                languageCode,
                // The primary language cannot also be a secondary one, and
                // auto detection makes the secondaries meaningless.
                secondaryLanguageCodes: languageCode
                  ? form.secondaryLanguageCodes.filter((code) => code !== languageCode)
                  : [],
              });
            }}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Secondary Languages</div>
          <div className="small-font">
            {form.languageCode
              ? 'Other languages the caller may switch to'
              : 'Available once an input language is selected'}
          </div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSelect
            id="secondary-languages-select"
            label="Secondary Languages"
            placeholder="Select secondary languages"
            options={secondaryLanguageOptions}
            value={form.secondaryLanguageCodes}
            multiSelect={true}
            disabled={isDisabled || !form.languageCode}
            onChange={(value) => set({ secondaryLanguageCodes: value as string[] })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Voice Speed</div>
          <div className="small-font">Set voice speed (0.7-1.2)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSlider
            id="voice-speed-slider"
            label="Voice Speed"
            value={form.voiceSpeed}
            min={0.7}
            max={1.2}
            oneStep={0.1}
            onChange={(value) => set({ voiceSpeed: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">VAD Threshold</div>
          <div className="small-font">Set VAD threshold (0.1-0.9)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSlider
            id="vad-threshold-slider"
            label="VAD Threshold"
            value={form.vadThreshold}
            min={0.1}
            max={0.9}
            oneStep={0.05}
            onChange={(value) => set({ vadThreshold: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">VAD Silence</div>
          <div className="small-font">Set VAD silence (0.5-5)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSlider
            id="vad-silence-slider"
            label="VAD Silence"
            value={form.vadSilence}
            min={0.3}
            max={3}
            oneStep={0.1}
            onChange={(value) => set({ vadSilence: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Noise Reduction</div>
          <div className="small-font">
            Filter background noise and other speakers out of the input
          </div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="toggle-filter-background-audio"
            label={form.filterBackgroundAudio ? 'Enabled' : 'Disabled'}
            checked={form.filterBackgroundAudio}
            onChange={(_id: string, checked: boolean) => set({ filterBackgroundAudio: checked })}
            disabled={isDisabled}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Stability</div>
          <div className="small-font">Set stability (0-1)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSlider
            id="stability-slider"
            label="Stability"
            value={form.stability}
            min={0}
            max={1}
            oneStep={0.05}
            onChange={(value) => set({ stability: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Similarity Boost</div>
          <div className="small-font">Set similarity boost (0-1)</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomSlider
            id="similarity-boost-slider"
            label="Similarity Boost"
            value={form.similarityBoost}
            min={0}
            max={1}
            oneStep={0.05}
            onChange={(value) => set({ similarityBoost: value })}
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Greeting message</div>
          <div className="small-font">Add greeting message for the call</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomTextarea
            id="greeting"
            label="Greeting message"
            name="greeting"
            addedText={form.greetingMessage}
            onChange={(value) => set({ greetingMessage: value })}
            resizable={false}
            disabled={isDisabled}
            height="200px"
          />
        </div>
      </div>

      <div className="input-block d-flex flex-row justify-between">
        <div className="input-description">
          <div className="input-title">Activate Voice Profile</div>
          <div className="small-font">Set voice profile to be active or inactive</div>
        </div>
        <div className="align-wrapper d-flex align-center">
          <CustomToggle
            id="toggle-status"
            label={form.isActive ? 'Active' : 'Inactive'}
            checked={form.isActive}
            onChange={(_id: string, checked: boolean) => set({ isActive: checked })}
            disabled={isDisabled}
          />
        </div>
      </div>
    </>
  );
}

export default VoiceProfileForm;
